<template>
  <v-card class="documents-table-card" outlined>
    <v-data-table
      class="elevation-0 documents-company-table"
      :headers="headers"
      :items="companies"
      :loading="loading"
      item-key="company_id"
      :group-by="groupByBroker ? 'broker_group' : null"
      sort-by="name"
      :custom-sort="customSort"
      :items-per-page="25"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
      @click:row="$emit('select', $event)"
    >
      <template #no-data>
        <span data-testid="documents-empty">{{ $t('documents.noCompanies') }}</span>
      </template>

      <!-- Broker group header, only rendered when grouping is on -->
      <template #[`group.header`]="{ group, headers: cols, toggle, isOpen, items }">
        <td :colspan="cols.length" class="documents-company-table__group" @click="toggle">
          <span v-if="trackGroup(group, toggle, isOpen)" />
          <div class="d-flex align-center py-1">
            <v-btn icon x-small class="mr-2 group-caret-btn" @click.stop="toggle">
              <v-icon small>{{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-icon small color="primary" class="mr-2">
              {{ isDirectGroup(group) ? 'mdi-account-multiple' : 'mdi-domain' }}
            </v-icon>
            <span class="font-weight-bold text-subtitle-2 mr-3">{{ group || '-' }}</span>
            <v-chip x-small color="primaryTransp" text-color="primary" class="font-weight-medium">
              {{ items ? items.length : 0 }} {{ $t(items && items.length === 1 ? 'documents.company' : 'documents.companies').toLowerCase() }}
            </v-chip>
          </div>
        </td>
      </template>

      <!-- Company name & CNPJ (clean corporate typography, no avatar) -->
      <template #[`item.name`]="{ item }">
        <div :class="['d-flex flex-column py-2', { 'company-tree-node': groupByBroker }]">
          <div class="d-flex align-center">
            <span class="font-weight-bold company-name-text">{{ item.name || '-' }}</span>
          </div>
          <div v-if="item.cnpj" class="caption grey--text mt-1">
            CNPJ: {{ convertToCNPJ(item.cnpj) }}
          </div>
        </div>
      </template>

      <!-- KYC documents count: green when > 0, orange when 0 -->
      <template #[`item.kyc_count`]="{ item }">
        <v-chip
          v-if="item.kyc_count"
          small
          color="aliveTransp"
          text-color="alive"
          class="font-weight-bold"
        >
          <v-icon x-small left color="alive">mdi-file-check-outline</v-icon>
          {{ item.kyc_count }}
        </v-chip>
        <v-chip
          v-else
          small
          color="yellowTransp"
          text-color="orangeColor"
          class="font-weight-bold"
        >
          <v-icon x-small left color="orangeColor">mdi-alert-circle-outline</v-icon>
          0
        </v-chip>
      </template>

      <!-- Operation documents count -->
      <template #[`item.operation_document_count`]="{ item }">
        <v-chip
          v-if="item.operation_document_count"
          small
          color="primaryTransp"
          text-color="primary"
          class="font-weight-bold"
        >
          <v-icon x-small left color="primary">mdi-file-document-multiple-outline</v-icon>
          {{ item.operation_document_count }}
        </v-chip>
        <span v-else class="grey--text text--darken-1">-</span>
      </template>

      <!-- Last upload -->
      <template #[`item.last_upload`]="{ item }">
        <date-time-layout v-if="item.last_upload" :timestamp="item.last_upload" />
        <span v-else class="grey--text text--darken-1">-</span>
      </template>

      <!-- Action -->
      <template #[`item.actions`]="{ item }">
        <div class="text-right">
          <v-btn
            small
            outlined
            color="primary"
            class="open-btn text-capitalize"
            data-testid="documents-open-company"
            @click.stop="$emit('select', item)"
          >
            {{ $t('documents.open') }}
            <v-icon small right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import DateTimeLayout from '@/components/common/DateTimeLayout.vue'
import convertToCNPJ from '@/utils.js'

export default {
  name: 'DocumentsCompanyTable',

  components: { DateTimeLayout },

  props: {
    companies: { type: Array, default: () => [] },
    loading:   { type: Boolean, default: false },
    // A master sees several brokers' clients, so grouping by broker turns the
    // same payload into the partner breakdown without a third page.
    groupByBroker: { type: Boolean, default: false },
    isBroker:      { type: Boolean, default: false },
  },

  emits: ['select'],

  computed: {
    headers () {
      const firstCol = (this.isBroker || this.groupByBroker)
        ? this.$t('documents.partner')
        : this.$t('documents.company')
      return [
        { text: firstCol,                                value: 'name' },
        { text: this.$t('documents.kycDocuments'),       value: 'kyc_count', align: 'center', width: '200px' },
        { text: this.$t('documents.operationDocuments'), value: 'operation_document_count', align: 'center', width: '220px' },
        { text: this.$t('documents.lastUpload'),         value: 'last_upload', width: '240px' },
        { text: '',                                      value: 'actions', sortable: false, align: 'end', width: '120px' },
      ]
    },
  },

  created () {
    this.groupMap = new Map()
  },

  methods: {
    trackGroup (group, toggle, isOpen) {
      if (group) {
        this.groupMap.set(group, { toggle, isOpen })
      }
      return false
    },

    expandAll () {
      if (this.groupMap && this.groupMap.size > 0) {
        this.groupMap.forEach(state => {
          if (state && !state.isOpen && typeof state.toggle === 'function') {
            state.toggle()
          }
        })
      }
      this.$nextTick(() => {
        const btns = this.$el.querySelectorAll('.group-caret-btn')
        btns.forEach(btn => {
          const icon = btn.querySelector('.mdi-chevron-down')
          if (icon) btn.click()
        })
      })
    },

    collapseAll () {
      if (this.groupMap && this.groupMap.size > 0) {
        this.groupMap.forEach(state => {
          if (state && state.isOpen && typeof state.toggle === 'function') {
            state.toggle()
          }
        })
      }
      this.$nextTick(() => {
        const btns = this.$el.querySelectorAll('.group-caret-btn')
        btns.forEach(btn => {
          const icon = btn.querySelector('.mdi-chevron-up')
          if (icon) btn.click()
        })
      })
    },

    convertToCNPJ (cnpj) {
      return convertToCNPJ(cnpj)
    },

    isDirectGroup (group) {
      return group === this.$t('documents.directClients')
    },

    customSort (items, sortBy, sortDesc, locale) {
      return items.slice().sort((a, b) => {
        if (!sortBy || !sortBy.length) {
          return (a.name || '').localeCompare(b.name || '', locale)
        }

        for (let i = 0; i < sortBy.length; i++) {
          const sortKey = sortBy[i]
          let sortA = a[sortKey]
          let sortB = b[sortKey]

          if (sortDesc && sortDesc[i]) {
            [sortA, sortB] = [sortB, sortA]
          }

          if (sortA !== sortB) {
            if (sortA == null) return 1
            if (sortB == null) return -1
            if (typeof sortA === 'string' && typeof sortB === 'string') {
              return sortA.localeCompare(sortB, locale)
            }
            if (typeof sortA === 'number' && typeof sortB === 'number') {
              return sortA - sortB
            }
            return sortA > sortB ? 1 : -1
          }
        }
        return 0
      })
    },
  },
}
</script>

<style scoped lang="scss">
.documents-table-card {
  background: #1e1e1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.documents-company-table {
  background: transparent !important;

  ::v-deep tbody tr:not(.v-row-group__header) {
    background: rgba(255, 255, 255, 0.02) !important;
    cursor: pointer;
    transition: background 0.15s ease-in-out;

    &:hover {
      background: rgba(255, 255, 255, 0.06) !important;
    }
  }

  /* Group header: transparent matching card surface, swapped with dropdown row tone */
  ::v-deep tr.v-row-group__header {
    background: transparent !important;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s ease-in-out;

    &:hover {
      background: rgba(255, 255, 255, 0.03) !important;
    }

    td,
    td.documents-company-table__group {
      padding: 0.65rem 24px !important;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
      background: transparent !important;
    }
  }

  ::v-deep thead th {
    font-size: 0.8rem !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 0.6) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
    white-space: nowrap !important;

    .v-data-table-header__icon {
      display: inline-flex !important;
      vertical-align: middle !important;
      margin-left: 4px !important;
    }
  }

  ::v-deep tbody td {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}

.group-caret-btn {
  width: 20px !important;
  min-width: 20px !important;
  height: 20px !important;
}

.company-name-text {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: #ffffff;
  letter-spacing: -0.01em;
}

.company-tree-node {
  padding-left: 36px;
}

.pending-chip {
  font-size: 10px !important;
  height: 20px !important;
}

.open-btn {
  border-color: rgba(0, 115, 255, 0.4) !important;
  font-size: 11px !important;
  font-weight: 600;
  letter-spacing: 0.02em;

  &:hover {
    background: rgba(0, 115, 255, 0.1) !important;
    border-color: #0073ff !important;
  }
}
</style>
